import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { money } from "@/data/site";

export function SearchOverlay() {
  const { searchOpen, setSearchOpen } = useApp();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (searchOpen) setTimeout(() => inputRef.current?.focus(), 80);
    else { setQ(""); setResults([]); }
  }, [searchOpen]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/products", { params: { search: q, limit: 6 } });
        setResults(data);
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setSearchOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setSearchOpen]);

  if (!searchOpen) return null;
  return (
    <div className="fixed inset-0 z-50" data-testid="search-overlay">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={() => setSearchOpen(false)} />
      <div className="relative mx-auto mt-24 max-w-2xl px-4 reveal">
        <div className="flex items-center gap-3 border-b-2 border-gold bg-ink px-4 py-4">
          <Search size={20} className="text-gold" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input"
            placeholder="Search HYEAMAN…" className="flex-1 bg-transparent text-lg text-cream placeholder:text-mutedgray outline-none" />
          <button onClick={() => setSearchOpen(false)} aria-label="Close search" className="text-cream/60 hover:text-gold"><X size={20} /></button>
        </div>
        <div className="mt-2 bg-ink border border-white/10">
          {loading && <p className="p-6 text-center text-sm text-mutedgray">Searching…</p>}
          {!loading && q && results.length === 0 && <p className="p-6 text-center text-sm text-mutedgray" data-testid="search-empty">No pieces match "{q}"</p>}
          {results.map((p) => (
            <Link key={p.id} to={`/product/${p.slug}`} onClick={() => setSearchOpen(false)} data-testid={`search-result-${p.slug}`}
              className="flex items-center gap-4 border-b border-white/5 px-4 py-3 transition hover:bg-elevated">
              <img src={p.images?.[0]} alt={p.name} className="h-14 w-11 object-cover border border-white/10" />
              <div className="flex-1">
                <p className="text-sm text-cream">{p.name}</p>
                <p className="text-xs text-mutedgray capitalize">{p.department} · {p.category}</p>
              </div>
              <span className="font-mono text-sm text-gold">{money(p.price)}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
